const API_BASE_URL = process.env.REACT_APP_WEDDING_API_URL || 'http://localhost:5001/api';

export interface GuestNote {
  _id?: string | number;
  id?: string | number; // Fallback for PostgreSQL id
  text: string;
  author: string;
  color: string;
  isApproved: boolean;
  createdAt: string;
}

export interface CreateGuestNoteRequest {
  text: string;
  author: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: any[];
}

class WeddingApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      console.log('Making API request to:', url);
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API response error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API response success:', data);
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the backend server. Please make sure the server is running on port 5001.');
      }
      throw error;
    }
  }

  // Normalize guest note to ensure _id is always present
  private normalizeGuestNote(note: any): GuestNote {
    return {
      ...note,
      _id: note._id || note.id || String(Date.now()),
      id: note.id || note._id
    };
  }

  // Guest Notes API
  async getGuestNotes(): Promise<GuestNote[]> {
    try {
      const response = await this.request<GuestNote[]>('/guest-notes');
      const notes = response.data || [];
      // Normalize all notes to ensure _id is present
      return notes.map(note => this.normalizeGuestNote(note));
    } catch (error) {
      console.error('Failed to fetch guest notes:', error);
      throw new Error('Failed to fetch guest notes');
    }
  }

  async createGuestNote(noteData: CreateGuestNoteRequest): Promise<GuestNote> {
    try {
      const response = await this.request<GuestNote>('/guest-notes', {
        method: 'POST',
        body: JSON.stringify(noteData),
      });

      if (!response.data) {
        throw new Error('No data returned from server');
      }

      // Normalize the response to ensure _id is present
      return this.normalizeGuestNote(response.data);
    } catch (error) {
      console.error('Failed to create guest note:', error);
      throw new Error('Failed to create guest note');
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}

// Create and export a singleton instance
export const weddingApi = new WeddingApiService();

// Export the class for testing purposes
export default WeddingApiService;
